import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { decodeImpersonationToken, type ImpersonationJwtClaims } from '@/lib/auth';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantStore } from '@/stores/tenant.store';
import { apiGet, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import type { CompanySettings, UserDetail } from '@/types/settings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

/**
 * Landing page for the platform-admin impersonation handoff.
 *
 * Flow:
 *   1. Provisioner mints a 2-hour JWT via operations.
 *   2. Provisioner opens a new tab to /auth/impersonate-handoff?token=<jwt>.
 *   3. This page validates the JWT claims (no signature check; the API does
 *      that on the first authenticated call), wipes any prior session, drops
 *      the token into the auth store, fetches the impersonated user + tenant
 *      for display, then redirects to /dashboard/home.
 *
 * Security note: the token rides in the URL, which leaks via Referer to any
 * external resource loaded by the first page render. We replace the URL with
 * `history.replaceState` synchronously on mount, BEFORE React schedules any
 * navigation or fetch, so anything that loads later sees a clean URL. The
 * token's 2-hour TTL bounds what's left of the exposure; note it is a
 * bearer token, not single-use, so it stays replayable until it expires
 * or the session is ended via /auth/impersonate/end.
 */

const searchSchema = z.object({
  token: z.string().min(1).optional(),
});

export const Route = createFileRoute('/(public)/auth/impersonate-handoff')({
  validateSearch: searchSchema,
  component: ImpersonateHandoffPage,
});

interface HandoffError {
  title: string;
  detail: string;
}

function ImpersonateHandoffPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<HandoffError | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // One-shot guard. Stripping the token from the URL below mutates
    // window.history, which TanStack Router intercepts and re-syncs — that
    // re-render hands us `token === undefined` and, because `token` is in the
    // dependency array, would re-run this effect straight into the "no token"
    // branch: a spurious failure toast layered on top of a successful handoff.
    // The same guard also absorbs StrictMode's double-invoke in dev. The
    // first run captured the real `token` in its closure, so it is unaffected.
    if (startedRef.current) return;
    startedRef.current = true;

    // Token-in-URL leaks via Referer to any external resource (analytics,
    // images, fonts, …) that loads while the URL still contains it. Strip
    // it immediately so the very next paint shows a clean URL. We can't do
    // this in `beforeLoad` because TanStack Router would then re-parse the
    // search on the next nav and lose the value — but by the time we reach
    // this effect we have already captured `token` into a closure.
    try {
      window.history.replaceState(null, '', '/auth/impersonate-handoff');
    } catch {
      // Old browsers without history.replaceState — best-effort only.
    }

    if (!token) {
      redirectWithError(
        navigate,
        'No impersonation token was provided. Open this tab from the provisioner.',
      );
      return;
    }

    const claims = decodeImpersonationToken(token);
    if (!claims) {
      redirectWithError(
        navigate,
        'The impersonation token is invalid or missing the required claims.',
      );
      return;
    }

    if (claims.exp * 1000 < Date.now()) {
      redirectWithError(
        navigate,
        'The impersonation token has already expired. Request a new one.',
      );
      return;
    }

    void completeHandoff({ token, claims, navigate, setError });
  }, [token, navigate]);

  if (error) {
    return <HandoffErrorCard error={error} />;
  }

  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl font-semibold">Starting impersonation session…</CardTitle>
          <CardDescription>Validating credentials and loading the workspace.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          <span>Please wait — this should only take a moment.</span>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}

function HandoffErrorCard({ error }: { error: HandoffError }) {
  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-md border-destructive/30">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            <CardTitle className="text-xl font-semibold">{error.title}</CardTitle>
          </div>
          <CardDescription>{error.detail}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Return to the provisioner and request a fresh impersonation session, or close this tab
            and sign in normally.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="default">
              <Link to="/login">Go to login</Link>
            </Button>
            {/* document.referrer is the safest "back to where you came from"
                fallback when we don't know which provisioner instance opened
                us — staging, prod, or local. */}
            {typeof document !== 'undefined' && document.referrer && (
              <Button asChild variant="outline">
                <a href={document.referrer}>Back to provisioner</a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}

interface CompleteHandoffArgs {
  token: string;
  claims: ImpersonationJwtClaims;
  navigate: ReturnType<typeof useNavigate>;
  setError: (err: HandoffError) => void;
}

async function completeHandoff({ token, claims, navigate, setError }: CompleteHandoffArgs) {
  const authStore = useAuthStore.getState();

  // 1. Wipe any previous session BEFORE writing the new one. logout() is
  //    idempotent and clears all derivative stores (socket/voip/tenant); we
  //    want a clean slate so feature flags from the operator's normal tenant
  //    can't bleed into the impersonated session.
  authStore.logout();

  // 2. Install the impersonation token. From this point onwards the axios
  //    interceptor will attach it to every operations request, so the
  //    follow-up fetches below are already authenticated as the target user.
  authStore.startImpersonation({
    userId: claims.sub,
    email: claims.email,
    token,
    tenantId: claims.tenantId,
    impersonatedBy: claims.impersonatedBy,
    impersonationId: claims.impersonationId,
    expiresAt: new Date(claims.exp * 1000).toISOString(),
    roles: claims.roles,
    permissions: claims.permissions,
    permissionsVersion: claims.permissionsVersion,
  });

  // 3. Best-effort enrichment: fetch the user + company so the sidebar/banner
  //    render real names instead of UUIDs. Failures here don't block the
  //    redirect — the rest of the app will lazy-load the same data when it
  //    needs it; we just lose the first-paint niceties.
  //
  //    Use `/users/me` instead of `/users/:id` here: the impersonated user is
  //    frequently NOT an admin (technician, dispatcher, owner of a small
  //    shop), so they don't have `users:view` and would 403 on the generic
  //    getter. `me` requires only authentication.
  let resolvedEmail: string | null = claims.email ?? null;
  try {
    const [user, company] = await Promise.all([
      apiGet<UserDetail>(operationsClient, settingsEndpoints.userMe()).catch(() => null),
      apiGet<CompanySettings>(operationsClient, settingsEndpoints.company()).catch(() => null),
    ]);

    // Prefer server truth (user.email); fall back to the JWT's email claim
    // when the server fetch fails — both are server-issued and cannot be
    // forged client-side, so this is strictly better than rendering the
    // user UUID.
    resolvedEmail = user?.email ?? claims.email ?? null;
    if (resolvedEmail) {
      useAuthStore.setState({ email: resolvedEmail });
    }

    if (company) {
      // Seed the tenant store so feature-flag checks have a tenant context.
      // We don't know the plan from the company endpoint, so default to
      // 'pro' — the same default used by the normal login path.
      useTenantStore.getState().setTenant({
        tenantId: claims.tenantId,
        tenantName: company.name ?? '',
        plan: 'pro',
      });
    } else {
      useTenantStore.getState().setTenant({
        tenantId: claims.tenantId,
        tenantName: '',
        plan: 'pro',
      });
    }
  } catch (err) {
    // We've already committed the impersonation session, so we still
    // navigate to the dashboard — the UI will just show fallback labels.
    console.warn('[impersonation] enrichment fetch failed; using fallback labels', err);
  }

  // 4. Land on the dashboard. We use `replace` so the back button doesn't
  //    take the operator back to this handoff page (which would no-op
  //    because the token has been stripped from the URL).
  void navigate({ to: '/dashboard/home', replace: true });

  // Belt-and-suspenders: state-change-driven re-renders inside the dashboard
  // mean some users only see the banner after their first interaction.
  // Surface a one-shot toast so it's unmissable on arrival. Use the email
  // resolved during enrichment (or the JWT claim) — never the bare UUID,
  // which is the user-visible bug we're fixing.
  toast.success(`Impersonating ${resolvedEmail ?? claims.sub}`, {
    description: `Authorised by ${claims.impersonatedBy}. Actions are logged.`,
    duration: 6000,
  });

  // Failsafe for use of `setError` after success — keeps the linter happy
  // and short-circuits in case completeHandoff is ever called twice for
  // the same token (StrictMode double-invoke in dev).
  void setError;
}

function redirectWithError(navigate: ReturnType<typeof useNavigate>, message: string) {
  toast.error('Impersonation handoff failed', { description: message });
  void navigate({ to: '/login', replace: true });
}
